
import { HttpParams, httpResource } from '@angular/common/http';
import { Component, effect, inject, signal, untracked, } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, Params, Router, RouterLink } from '@angular/router';
import { ListComponent } from '@components/list/list.component';
import { environment } from '@environment';
import { ItemListResponse, Placename } from '@models';
import { AuthService, NotificationService, PlacenameService } from '@services';
import { PlacenameSearchComponent } from '../search/search.component';
import { MatChipsModule } from '@angular/material/chips';
import { TitleCasePipe } from '@angular/common';

@Component({
  selector: 'app-placename-list',
  templateUrl: './list.component.html',
  styleUrl: './list.component.scss',
  imports: [
    MatListModule,
    MatPaginatorModule,
    MatButtonModule,
    MatIconModule,
    MatTabsModule,
    MatChipsModule,
    FormsModule,
    RouterLink,
    ListComponent,
    TitleCasePipe,
  ],
})
export class PlacenameListComponent {
  authService = inject(AuthService);
  placenameService = inject(PlacenameService);
  notificationService = inject(NotificationService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  dialog = inject(MatDialog);

  q = signal<string>(this.route.snapshot.queryParams['q'] || '');
  location = signal<string>(this.route.snapshot.queryParams['location'] || '');
  status = signal<string>(this.route.snapshot.queryParams['status'] || '');
  area = signal<string>(this.route.snapshot.queryParams['area'] || '');
  page = signal(parseInt(this.route.snapshot.queryParams['page']) || 0);
  pageSize = signal(parseInt(this.route.snapshot.queryParams['pageSize']) || 10);

  count = signal(0);
  placenames = httpResource<ItemListResponse<Placename>>(() => {
    const count = untracked(() => this.count() === 0 || this.page() === 0);
    let httpParams = new HttpParams()
      .set('skip', this.page() * this.pageSize())
      .set('take', this.pageSize())
      .set('count', count)
      .set('order', 'created:desc');

    if (this.location()) {
      httpParams = httpParams.set('location', this.location());
    }

    if (this.status()) {
      httpParams = httpParams.set('status', this.status());
    }

    if (this.area()) {
      httpParams = httpParams.set('area', this.area());
    }

    if (this.q()) {
      httpParams = httpParams.set('q', this.q());
    }

    return `${environment.kinkoHost}/placename/?${httpParams.toString()}`;
  });

  constructor() {
    effect(() => {
      if (!this.placenames.hasValue() || !this.placenames.value().count) {
        return;
      }

      this.count.set(this.placenames.value().count);
    });

    this.route.queryParams.subscribe((params: Params) => {
      this.page.set(parseInt(params['page']) || 0);
      this.pageSize.set(parseInt(params['pageSize']) || 10);
      this.q.set(params['q'] || '');
      this.location.set(params['location'] || '');
      this.status.set(params['status'] || '');
      this.area.set(params['area'] || '');
    });
  }

  // undefined page size uses this.pageSize()
  search(q: string, location: string, status: string, area: string, page: number, pageSize?: number) {
    const take = pageSize === undefined ? this.pageSize() : pageSize;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: {
        q: q || undefined,
        location: location || undefined,
        status: status || undefined,
        area: area || undefined,
        page: page || undefined,
        pageSize: take === 10 ? undefined : take,
      },
      replaceUrl: true
    });
  }

  openSearch() {
    this.dialog.open(PlacenameSearchComponent, {
      data: {
        search: this.q(),
        location: this.location(),
        status: this.status(),
        area: this.area(),
      },
      minWidth: '360px',
      maxWidth: '960px',
    }).afterClosed().subscribe((data?: {
      search: string,
      location: string,
      status: string,
      area: string,
    }) => {
      if (!data) {
        return;
      }

      this.search(
        data.search, data.location,
        data.status, data.area,
        0, this.pageSize(),
      );
    });
  }
}
